import BreadCrumbs from "@/components/common/bread-crumbs"
import ButtonGoTo from "@/components/common/button-go-to"
import CommonContentTitle from "@/components/common/content-title"
import ErrorMessage from "@/components/common/error-message"
import { getJobItem } from "@/services/microcms"
import type { Job } from "@/types/Job"
import { getTemplatedMetaArray } from "@/utils/getTemplatedMetaArray"
import { type MetaArgs, useLocation } from "react-router"
import type { Route } from "./+types/_default.recruit_.$jobId"

export const meta = ({
  data,
  matches,
}: { data: Route.MetaArgs["data"]; matches: MetaArgs["matches"] }) => {
  const jobData = data?.data
  return getTemplatedMetaArray(matches, {
    title: `採用情報${jobData?.jobTitle ? ` | ${jobData.jobTitle}` : ""} | コタエルHR`,
    description:
      jobData?.summary ||
      "採用情報ページです。コタエルHRは人と組織にまつわる課題を解決し、成長を目指す企業の未来をつくります。さまざまなHRサービスの提供を通じて、組織の進化を加速させます。",
  })
}

export const loader = async ({ params, request }: Route.LoaderArgs) => {
  const queryStrings = new URLSearchParams(request.url.split("?")[1])
  const queries = Object.fromEntries(queryStrings)
  const draftKey = queries.draftKey

  return await getJobItem(params.jobId, draftKey)
}

export default function JobDetail({
  params,
  loaderData,
}: Route.ComponentProps) {
  const location = useLocation()
  const queryStrings = new URLSearchParams(location.search)
  const queries = Object.fromEntries(queryStrings)
  const draftKey = queries?.draftKey

  const error = (loaderData as { error: unknown })?.error
  const job = loaderData?.data as Job
  if (error || !job) {
    return (
      <>
        <CommonContentTitle title="採用情報" titleEn="Recruit" />
        <div className="mb-20">
          <ErrorMessage message="求人情報" />
        </div>
      </>
    )
  }

  const firstSectionItems = [
    {
      title: "主な業務内容",
      text: job?.jobDescription,
    },
    {
      title: "ポジションの魅力",
      text: job?.merits,
    },
    {
      title: "応募資格（必須）",
      text: job?.skillsRequired,
    },
    {
      title: "応募資格（歓迎）",
      text: job?.skillsPreferred,
    },
    {
      title: "求める人材像",
      text: job?.idealProfile,
    },
    {
      title: "選考フロー",
      text: job?.steps,
    },
  ] as const

  const secondSectionItems = [
    {
      title: "給与",
      text: job?.salary,
    },
    {
      title: "勤務地",
      text: job?.location,
    },
    {
      title: "雇用形態",
      text: job?.employmentType,
    },
    {
      title: "勤務体系",
      text: job?.workArrangement,
    },
    {
      title: "試用期間",
      text: job?.trialPeriod,
    },
    {
      title: "福利厚生",
      text: job?.benefits,
    },
  ] as const

  const breadCrumbsItems = [
    {
      name: "HOME",
      url: "/",
    },
    {
      name: "RECRUIT",
      url: "/recruit",
    },
    {
      name: job.jobTitle,
      url: "",
    },
  ]

  return (
    <>
      <CommonContentTitle title="採用情報" titleEn="Recruit" />

      <div
        className={`
          pt-10 pb-8
          md:pt-[102px] md:mb-20
          px-[30px] md:px-40
          flex flex-col
          tracking-[0.03em]
        `}
      >
        <section>
          <h2 className="text-[18px] md:text-[24px] md:leading-[1.6] md:mb-10">
            {job.jobTitle}
          </h2>

          <ul className="mt-5 mb-8 md:my-10">
            {firstSectionItems.map((item) => (
              <li
                key={item.title}
                className={`
                  grid
                  grid-cols-1 md:grid-cols-[140px,auto]
                  gap-y-4 md:gap-y-1.5 md:gap-x-16
                  py-3.5 md:p-5
                  border-b first:border-t
                  border-main
                `}
              >
                <h3 className="text-[14px] leading-[1.6em] font-semibold">
                  {item.title}
                </h3>

                <div
                  // biome-ignore lint/security/noDangerouslySetInnerHtml:
                  dangerouslySetInnerHTML={{
                    __html: textToHtml(item.text),
                  }}
                  className={`
                    text-[14px] leading-[1.8]
                  `}
                />
              </li>
            ))}
          </ul>
        </section>

        <section>
          <h3
            className={`
              text-[16px] md:text-[20px]
              leading-[1.6]
              font-semibold
              mb-5 md:mt-10 md:mb-10
            `}
          >
            募集要項
          </h3>

          <ul className="mt-5 md:my-10">
            {secondSectionItems.map((item) => (
              <li
                key={item.title}
                className={`
                  grid
                  grid-cols-1 md:grid-cols-[140px,auto]
                  gap-y-4 md:gap-y-1.5 md:gap-x-16
                  py-3.5 md:p-5
                  border-b first:border-t
                  border-main
                `}
              >
                <h3 className="text-[14px] leading-[1.6em] font-semibold">
                  {item.title}
                </h3>

                <div
                  // biome-ignore lint/security/noDangerouslySetInnerHtml:
                  dangerouslySetInnerHTML={{
                    __html: textToHtml(item.text),
                  }}
                  className={`
                    text-[14px] leading-[1.8]
                  `}
                />
              </li>
            ))}
          </ul>
        </section>
      </div>

      <div className="mb-16 md:mb-52 px-[30px] md:px-0">
        <ButtonGoTo
          to={`/recruit/${params.jobId}/apply${draftKey ? `?draftKey=${draftKey}` : ""}`}
          text="応募する"
          variant="main"
          variantHover="outline-main"
          className={`
            place-self-center
            w-full
            h-[60px] md:min-w-[280px]
            [&_span]:text-[16px]
          `}
          target="_blank"
        />
      </div>

      <BreadCrumbs items={breadCrumbsItems} />
    </>
  )
}

const textToHtml = (text: string | undefined) =>
  text
    // 二重の改行を<p>タグに変換することで、二重の<br>を回避
    ?.split("\n\n")
    .map(
      (p) => `<p class="mb-3 last:mb-0 md:[&:not(:last-child)]:mb-6">${p}</p>`,
    )
    .join("")
    .replace(/\n/g, "<br/>") ?? ""
